import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { useDataProvider, useNotify } from "react-admin";
import type { AgentRelatedOrder } from "../../models/AgentDispatchRecord";
import type { Waybill } from "../../models/Waybill";
import {
  getOrderStatusColor,
  getOrderStatusTitle,
} from "../../constants/OrderStatus";
import { formatDate } from "../../utils/common";
import {
  AgentCargoInfoView,
  formatCargoAddress,
  formatCargoPrice,
  formatCargoRoute,
  parseCargoRecord,
} from "./agent_cargo_info";

/**
 * 格式化时间展示。
 */
const formatDateValue = (value: string | undefined | null): string => {
  if (!value) {
    return "-";
  }
  return formatDate(value);
};

/**
 * 格式化装/卸线路。
 */
const formatWaybillRoute = (waybill: Waybill | null): string => {
  if (!waybill) {
    return "-";
  }
  const from = waybill.from
    ? [waybill.from.city, waybill.from.district].filter(Boolean).join("·")
    : "";
  const to = waybill.to
    ? [waybill.to.city, waybill.to.district].filter(Boolean).join("·")
    : "";
  if (!from && !to) {
    return "-";
  }
  return `${from || "-"} → ${to || "-"}`;
};

/**
 * 布尔标记 Chip。
 */
const BoolChip: React.FC<{ value: boolean; trueLabel?: string; falseLabel?: string }> = ({
  value,
  trueLabel = "是",
  falseLabel = "否",
}) => (
  <Chip
    label={value ? trueLabel : falseLabel}
    size="small"
    color={value ? "success" : "default"}
    variant={value ? "filled" : "outlined"}
    sx={{ height: 20, "& .MuiChip-label": { px: 0.75, fontSize: 12 } }}
  />
);

/**
 * 紧凑详情字段：标签与值同一行。
 */
const CompactField: React.FC<{ label: string; value: React.ReactNode; fullWidth?: boolean }> = ({
  label,
  value,
  fullWidth = false,
}) => (
  <Box
    sx={{
      minWidth: 0,
      display: "flex",
      gap: 0.75,
      alignItems: "baseline",
      gridColumn: fullWidth ? "1 / -1" : undefined,
    }}
  >
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ flexShrink: 0, whiteSpace: "nowrap", lineHeight: 1.4 }}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      component="div"
      sx={{
        minWidth: 0,
        wordBreak: "break-word",
        lineHeight: 1.4,
        fontSize: 13,
      }}
    >
      {value}
    </Typography>
  </Box>
);

/**
 * 区块标题。
 */
const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    variant="caption"
    color="text.secondary"
    sx={{
      fontWeight: 600,
      letterSpacing: 0.3,
      textTransform: "none",
      display: "block",
      mb: 0.75,
      mt: 0.25,
    }}
  >
    {children}
  </Typography>
);

/**
 * 从 getOne 响应中解析运单。
 */
const parseWaybillRecord = (raw: unknown): Waybill | null => {
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const record = raw as Record<string, unknown>;
  if (record.data && typeof record.data === "object" && !Array.isArray(record.data)) {
    return parseWaybillRecord(record.data);
  }
  const id = record.id ?? record._id;
  if (id === undefined || id === null || id === "") {
    return null;
  }
  return {
    ...(record as unknown as Waybill),
    id: String(id),
  };
};

export interface AgentRelatedOrderDetailDialogProps {
  open: boolean;
  order: AgentRelatedOrder | null;
  onClose: () => void;
}

/**
 * 关联订单详情弹窗：紧凑展示关联信息与订单详情。
 */
export const AgentRelatedOrderDetailDialog: React.FC<AgentRelatedOrderDetailDialogProps> = ({
  open,
  order,
  onClose,
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [waybill, setWaybill] = React.useState<Waybill | null>(null);

  React.useEffect(() => {
    if (!open || !order?.orderId) {
      setWaybill(null);
      setError("");
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchOrder = async () => {
      try {
        setLoading(true);
        setError("");
        setWaybill(null);
        const response = await dataProvider.getOne("waybills", { id: order.orderId });
        if (cancelled) {
          return;
        }
        const next = parseWaybillRecord(response.data);
        setWaybill(next);
        if (!next) {
          setError("未获取到订单详情");
        }
      } catch (fetchError) {
        if (cancelled) {
          return;
        }
        const message =
          fetchError instanceof Error ? fetchError.message : "获取订单详情失败";
        setError(message);
        setWaybill(null);
        notify(`获取订单详情失败：${message}`, { type: "error" });
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void fetchOrder();

    return () => {
      cancelled = true;
    };
  }, [dataProvider, notify, open, order?.orderId]);

  const cargo = React.useMemo(() => {
    if (!waybill?.cargo) {
      return null;
    }
    return parseCargoRecord(waybill.cargo);
  }, [waybill]);

  const fieldGridSx = {
    display: "grid",
    gridTemplateColumns: {
      xs: "1fr",
      sm: "repeat(2, minmax(0, 1fr))",
      md: "repeat(3, minmax(0, 1fr))",
    },
    columnGap: 1.5,
    rowGap: 0.75,
  } as const;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ py: 1.25, px: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mr: 0.5 }}>
            关联订单详情
          </Typography>
          {order && (
            <>
              <BoolChip
                value={order.orderCanceled}
                trueLabel="已取消"
                falseLabel="未取消"
              />
              <BoolChip
                value={order.orderCreatedAfterFirstPush}
                trueLabel="推送后创建"
                falseLabel="非推送后创建"
              />
              <BoolChip
                value={order.orderCreatedAfterFirstPushNonCanceled}
                trueLabel="推送后创建且未取消"
                falseLabel="非推送后创建未取消"
              />
              {typeof waybill?.status === "number" && (
                <Chip
                  label={getOrderStatusTitle(waybill.status)}
                  size="small"
                  sx={{
                    height: 20,
                    backgroundColor: getOrderStatusColor(waybill.status),
                    color: "#fff",
                    "& .MuiChip-label": { px: 0.75, fontSize: 12 },
                  }}
                />
              )}
            </>
          )}
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{ px: 2, py: 1.5 }}>
        {!order ? (
          <Typography variant="body2" color="text.secondary">
            暂无选中订单
          </Typography>
        ) : (
          <Stack spacing={1.25}>
            <Box>
              <SectionTitle>关联信息</SectionTitle>
              <Box sx={fieldGridSx}>
                <CompactField label="日期" value={order.dayKey || "-"} />
                <CompactField label="订单ID" value={order.orderId || "-"} />
                <CompactField label="推送次数" value={String(order.pushCount)} />
                <CompactField label="货源" value={order.cargoName || order.cargoId || "-"} />
                <CompactField label="司机" value={order.driverName || order.driverId || "-"} />
                <CompactField label="货主" value={order.shipperName || order.shipperId || "-"} />
                <CompactField label="车牌" value={order.truckPlate || "-"} />
                <CompactField label="推送渠道" value={order.pushChannels || "-"} />
                <CompactField label="订单状态" value={order.orderStatus || "-"} />
                <CompactField label="首次推送" value={formatDateValue(order.firstPushedAt)} />
                <CompactField label="末次推送" value={formatDateValue(order.lastPushedAt)} />
                <CompactField label="订单创建" value={formatDateValue(order.orderCreatedAt)} />
                {(order.cargoId || order.driverId || order.shipperId || order.truckId) && (
                  <CompactField
                    label="相关ID"
                    fullWidth
                    value={[
                      order.cargoId ? `货源 ${order.cargoId}` : "",
                      order.driverId ? `司机 ${order.driverId}` : "",
                      order.shipperId ? `货主 ${order.shipperId}` : "",
                      order.truckId ? `车辆 ${order.truckId}` : "",
                    ]
                      .filter(Boolean)
                      .join(" · ") || "-"}
                  />
                )}
                {(order.requestIds || order.reasons) && (
                  <>
                    {order.requestIds ? (
                      <CompactField label="请求ID" value={order.requestIds} fullWidth />
                    ) : null}
                    {order.reasons ? (
                      <CompactField label="原因" value={order.reasons} fullWidth />
                    ) : null}
                  </>
                )}
              </Box>
            </Box>

            <Box
              sx={{
                borderTop: "1px solid",
                borderColor: "divider",
                pt: 1.25,
              }}
            >
              <SectionTitle>订单详情</SectionTitle>

              {loading && (
                <Box display="flex" alignItems="center" gap={1} py={0.5}>
                  <CircularProgress size={16} />
                  <Typography variant="caption" color="text.secondary">
                    正在加载订单详情...
                  </Typography>
                </Box>
              )}

              {error && !loading && (
                <Alert severity="warning" sx={{ py: 0.5 }}>
                  {error}
                </Alert>
              )}

              {!loading && !error && waybill && (
                <Stack spacing={1}>
                  <Box sx={fieldGridSx}>
                    <CompactField label="运单ID" value={waybill.id || "-"} />
                    <CompactField
                      label="司机"
                      value={
                        [
                          waybill.driver?.name,
                          waybill.truck?.phone || waybill.driver?.phone,
                        ]
                          .filter(Boolean)
                          .join(" / ") || "-"
                      }
                    />
                    <CompactField
                      label="车辆"
                      value={
                        [waybill.truck?.plate, waybill.truck?.type].filter(Boolean).join(" · ") ||
                        "-"
                      }
                    />
                    <CompactField label="货主" value={waybill.owner?.name || "-"} />
                    <CompactField label="线路" value={formatWaybillRoute(waybill)} />
                    <CompactField
                      label="货源价"
                      value={cargo ? formatCargoPrice(cargo) : "-"}
                    />
                    <CompactField
                      label="装车地"
                      value={formatCargoAddress(waybill.from)}
                      fullWidth
                    />
                    <CompactField
                      label="卸车地"
                      value={formatCargoAddress(waybill.to)}
                      fullWidth
                    />
                    <CompactField label="创建" value={formatDateValue(waybill.createdAt)} />
                    <CompactField label="更新" value={formatDateValue(waybill.updatedAt)} />
                    {cargo && (
                      <CompactField
                        label="货源线路"
                        value={formatCargoRoute(cargo)}
                      />
                    )}
                  </Box>

                  {cargo && (
                    <Box
                      sx={{
                        borderTop: "1px dashed",
                        borderColor: "divider",
                        pt: 1,
                      }}
                    >
                      <SectionTitle>货源信息</SectionTitle>
                      <AgentCargoInfoView cargo={cargo} compact />
                    </Box>
                  )}
                </Stack>
              )}

              {!loading && !error && !waybill && !order.orderId && (
                <Typography variant="caption" color="text.secondary">
                  该记录缺少订单 ID，无法加载订单详情
                </Typography>
              )}
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1 }}>
        <Button size="small" onClick={onClose}>
          关闭
        </Button>
      </DialogActions>
    </Dialog>
  );
};
